import { useMemo, useState } from 'react'

type ConnectionState = 'configure' | 'saved' | 'sandbox' | 'live'

interface CarrierPreset {
  id: string
  name: string
  mark: string
  auth: string
  access: string
  capabilities: string[]
  verified?: boolean
}

const CARRIERS: CarrierPreset[] = [
  {
    id: 'cma-cgm',
    name: 'CMA CGM',
    mark: 'CM',
    auth: 'OAuth 2.0 client credentials',
    access: 'Official API · carrier UAT required for transactional access',
    capabilities: [
      'Rates',
      'Schedules',
      'Booking',
      'Amend / cancel',
      'Events',
      'Shipping instructions',
    ],
    verified: true,
  },
  {
    id: 'maersk',
    name: 'Maersk',
    mark: 'MA',
    auth: 'Carrier developer credentials',
    access: 'Adapter ready · account entitlement may be required',
    capabilities: ['Rates', 'Schedules', 'Booking', 'Events', 'Documents'],
  },
  {
    id: 'msc',
    name: 'MSC',
    mark: 'MS',
    auth: 'Carrier account credentials',
    access: 'Adapter ready · carrier onboarding required',
    capabilities: ['Schedules', 'Booking', 'Events', 'Documents'],
  },
  {
    id: 'hapag',
    name: 'Hapag-Lloyd',
    mark: 'HL',
    auth: 'Carrier developer credentials',
    access: 'Adapter ready · carrier onboarding required',
    capabilities: ['Rates', 'Schedules', 'Booking', 'Events', 'Documents'],
  },
  {
    id: 'one',
    name: 'ONE',
    mark: 'ON',
    auth: 'Carrier account credentials',
    access: 'Adapter template · endpoint validation required',
    capabilities: ['Schedules', 'Booking', 'Events', 'Documents'],
  },
  {
    id: 'cosco',
    name: 'COSCO Shipping',
    mark: 'CS',
    auth: 'Carrier account credentials',
    access: 'Adapter template · endpoint validation required',
    capabilities: ['Rates', 'Schedules', 'Booking', 'Events'],
  },
  {
    id: 'evergreen',
    name: 'Evergreen',
    mark: 'EV',
    auth: 'Carrier account credentials',
    access: 'Adapter template · endpoint validation required',
    capabilities: ['Schedules', 'Booking', 'Events'],
  },
  {
    id: 'zim',
    name: 'ZIM',
    mark: 'ZI',
    auth: 'Carrier developer credentials',
    access: 'Adapter template · endpoint validation required',
    capabilities: ['Rates', 'Schedules', 'Booking', 'Events'],
  },
  {
    id: 'yang-ming',
    name: 'Yang Ming',
    mark: 'YM',
    auth: 'Carrier account credentials',
    access: 'Adapter template · endpoint validation required',
    capabilities: ['Schedules', 'Booking', 'Events'],
  },
  {
    id: 'hmm',
    name: 'HMM',
    mark: 'HM',
    auth: 'Carrier account credentials',
    access: 'Adapter template · endpoint validation required',
    capabilities: ['Schedules', 'Booking', 'Events'],
  },
]

const STATE_COPY: Record<ConnectionState, { label: string; note: string }> = {
  configure: { label: 'Ready to configure', note: 'Add your carrier credentials' },
  saved: { label: 'Credentials saved', note: 'Run the sandbox check' },
  sandbox: { label: 'Sandbox verified', note: 'Production approval required' },
  live: { label: 'Production live', note: 'Syncing normally' },
}

export function CarrierConnections({
  onConnected,
  onMinimize,
}: {
  onConnected: (name: string) => void
  onMinimize: () => void
}) {
  const [selectedId, setSelectedId] = useState('cma-cgm')
  const [query, setQuery] = useState('')
  const [states, setStates] = useState<Record<string, ConnectionState>>({})
  const [environment, setEnvironment] = useState('Sandbox / UAT')
  const [clientId, setClientId] = useState('')
  const [secret, setSecret] = useState('')
  const [partyCode, setPartyCode] = useState('')
  const [notice, setNotice] = useState('')

  const carriers = useMemo(() => {
    const q = query.trim().toLowerCase()
    return CARRIERS.filter(
      (carrier) =>
        !q || `${carrier.name} ${carrier.capabilities.join(' ')}`.toLowerCase().includes(q)
    )
  }, [query])
  const selected = CARRIERS.find((carrier) => carrier.id === selectedId) ?? CARRIERS[0]
  const state = states[selected.id] ?? 'configure'

  const saveCredentials = () => {
    if (!clientId || !secret || !partyCode) {
      setNotice('Add client ID, secret and customer / party code first.')
      return
    }
    setStates((current) => ({ ...current, [selected.id]: 'saved' }))
    setNotice('Encrypted credentials saved. The secret will not be shown again.')
    setSecret('••••••••••••••••')
  }

  const testSandbox = () => {
    if (state === 'configure') {
      setNotice('Save credentials before testing the sandbox.')
      return
    }
    setStates((current) => ({ ...current, [selected.id]: 'sandbox' }))
    setNotice('Preview connection check passed. Complete carrier UAT before production activation.')
    onConnected(`${selected.name} sandbox`)
  }

  return (
    <div className="ccx">
      <div className="ccx-head">
        <div>
          <span>Carrier connection catalogue</span>
          <h4>Choose a line. Add credentials. Test safely.</h4>
          <p>One normalized connection layer for rates, bookings, events and documents.</p>
        </div>
        <div className="ccx-head-actions">
          <label>
            <span>⌕</span>
            <input
              aria-label="Find carrier connection"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Find carrier or capability"
            />
          </label>
          <button type="button" onClick={onMinimize} aria-label="Minimize carrier connections">
            <span>Minimize</span>⌃
          </button>
        </div>
      </div>

      <div className="ccx-workspace">
        <nav aria-label="Carrier presets">
          {carriers.map((carrier) => {
            const carrierState = states[carrier.id] ?? 'configure'
            return (
              <button
                type="button"
                className={selected.id === carrier.id ? 'active' : ''}
                onClick={() => {
                  setSelectedId(carrier.id)
                  setNotice('')
                }}
                key={carrier.id}
              >
                <i>{carrier.mark}</i>
                <span>
                  <b>{carrier.name}</b>
                  <small>{STATE_COPY[carrierState].label}</small>
                </span>
                <em className={`is-${carrierState}`} />
              </button>
            )
          })}
          <button
            type="button"
            className="ccx-custom"
            onClick={() =>
              setNotice('Use Partner feed for a custom API, webhook, SFTP or email source.')
            }
          >
            <i>＋</i>
            <span>
              <b>Other carrier</b>
              <small>Start a custom adapter</small>
            </span>
            <em>→</em>
          </button>
        </nav>

        <main>
          <header>
            <div className="ccx-identity">
              <i>{selected.mark}</i>
              <div>
                <span>Selected carrier</span>
                <h5>{selected.name}</h5>
              </div>
            </div>
            <div className={`ccx-state is-${state}`}>
              <i />
              <span>
                <b>{STATE_COPY[state].label}</b>
                <small>{STATE_COPY[state].note}</small>
              </span>
            </div>
          </header>

          <div className={`ccx-readiness ${selected.verified ? 'is-mapped' : ''}`}>
            <span>
              {selected.verified ? 'Official specification mapped' : 'Connection shell ready'}
            </span>
            <small>
              {selected.verified
                ? 'Credentials and carrier approval are still required.'
                : 'Carrier-specific backend adapter will be implemented later.'}
            </small>
          </div>

          <div className="ccx-facts">
            <div>
              <span>Authentication</span>
              <b>{selected.auth}</b>
            </div>
            <div>
              <span>Access path</span>
              <b>{selected.access}</b>
            </div>
          </div>

          <div className="ccx-capabilities">
            <span>Available modules</span>
            <div>
              {selected.capabilities.map((capability) => (
                <b key={capability}>✓ {capability}</b>
              ))}
            </div>
          </div>

          <div className="ccx-form">
            <label>
              <span>Environment</span>
              <select
                aria-label="Carrier environment"
                value={environment}
                onChange={(event) => setEnvironment(event.target.value)}
              >
                <option>Sandbox / UAT</option>
                <option>Production</option>
              </select>
            </label>
            <label>
              <span>Client ID / API key</span>
              <input
                aria-label="Carrier client ID"
                value={clientId}
                onChange={(event) => setClientId(event.target.value)}
                placeholder="Issued by carrier"
                autoComplete="off"
              />
            </label>
            <label>
              <span>Client secret</span>
              <input
                aria-label="Carrier client secret"
                value={secret}
                onChange={(event) => setSecret(event.target.value)}
                placeholder="Stored encrypted"
                type="password"
                autoComplete="new-password"
              />
            </label>
            <label>
              <span>Customer / party code</span>
              <input
                aria-label="Carrier party code"
                value={partyCode}
                onChange={(event) => setPartyCode(event.target.value)}
                placeholder="e.g. 0012345678"
              />
            </label>
          </div>

          <div className="ccx-security">
            <i>⌾</i>
            <p>
              <b>Secrets stay sealed.</b> Credentials are encrypted, masked after saving and
              separated by environment. FreightRatesMCP never marks a connection live until its
              sandbox check and carrier approval are complete.
            </p>
          </div>

          {notice && <output className="ccx-notice">{notice}</output>}

          <footer>
            <button type="button" className="ccx-secondary" onClick={saveCredentials}>
              Save encrypted credentials
            </button>
            <button type="button" className="ccx-primary" onClick={testSandbox}>
              Test in sandbox →
            </button>
          </footer>
        </main>
      </div>
      <style>{styles}</style>
    </div>
  )
}

const styles = `
.ccx{background:var(--surface-1);border-top:1px solid var(--border);color:var(--text-primary)}.ccx *{box-sizing:border-box}.ccx button,.ccx input,.ccx select{font:inherit}.ccx-head{display:flex;align-items:end;justify-content:space-between;gap:20px;padding:18px}.ccx-head>div>span,.ccx-capabilities>span,.ccx-form label>span,.ccx-facts span,.ccx-identity span{font-family:var(--mono);font-size:8px;letter-spacing:.1em;text-transform:uppercase;color:var(--text-muted)}.ccx-head>div>span{color:var(--frd-accent);font-weight:800}.ccx-head h4{font-family:var(--font-display);font-size:21px;letter-spacing:-.02em;margin:4px 0}.ccx-head p{font-size:10px;color:var(--text-muted);margin:0}.ccx-head-actions{display:flex;align-items:center;gap:8px}.ccx-head-actions>label{width:250px;display:flex;align-items:center;border:1px solid var(--border);background:var(--surface-0);border-radius:10px;padding:0 10px;box-shadow:var(--shadow-card)}.ccx-head-actions>label span{color:var(--text-muted)}.ccx-head-actions>label input{border:0;background:transparent;color:inherit;outline:0;width:100%;padding:10px;font-size:10px}.ccx-head-actions>label:focus-within{border-color:var(--frd-accent);box-shadow:0 0 0 3px color-mix(in srgb,var(--frd-accent) 12%,transparent)}.ccx-head-actions>button{display:flex;align-items:center;gap:6px;border:1px solid var(--border);background:var(--surface-0);color:var(--text-muted);border-radius:10px;padding:10px 12px;font-size:9px;font-weight:700;cursor:pointer}.ccx-head-actions>button:hover{color:var(--text-primary);border-color:color-mix(in srgb,var(--frd-accent) 45%,var(--border))}.ccx-workspace{display:grid;grid-template-columns:240px 1fr;border-top:1px solid var(--border);min-height:468px}.ccx nav{padding:10px;border-right:1px solid var(--border);max-height:520px;overflow:auto}.ccx nav button{width:100%;display:grid;grid-template-columns:34px 1fr 7px;gap:9px;align-items:center;border:0;background:transparent;color:inherit;text-align:left;padding:9px;border-radius:9px;cursor:pointer;transition:background .16s ease,box-shadow .16s ease}.ccx nav button:hover,.ccx nav button.active{background:var(--surface-0)}.ccx nav button.active{box-shadow:inset 3px 0 var(--frd-accent),var(--shadow-card)}.ccx nav button>i,.ccx-identity>i{width:34px;height:34px;border-radius:9px;display:grid;place-items:center;background:var(--text-primary);color:var(--surface-0);font-family:var(--mono);font-size:9px;font-style:normal;font-weight:800}.ccx nav span b,.ccx nav span small{display:block}.ccx nav span b{font-size:10px}.ccx nav span small{font-size:8px;color:var(--text-muted);margin-top:2px}.ccx nav button>em{width:7px;height:7px;border-radius:50%;background:var(--surface-2)}.ccx nav button>em.is-live{background:#10b981}.ccx nav button>em.is-sandbox,.ccx nav button>em.is-saved{background:#f59e0b}.ccx nav .ccx-custom{margin-top:9px;border-top:1px solid var(--border);border-radius:0;padding-top:15px}.ccx nav .ccx-custom>i{background:transparent;color:var(--frd-accent);border:1px dashed var(--border)}.ccx nav .ccx-custom>em{width:auto;height:auto;background:none;font-style:normal;color:var(--frd-accent)}.ccx main{background:var(--surface-0);padding:18px 20px}.ccx main>header{display:flex;align-items:center;justify-content:space-between;padding-bottom:14px;border-bottom:1px solid var(--border)}.ccx-identity{display:flex;align-items:center;gap:10px}.ccx-identity h5{font-family:var(--font-display);font-size:20px;letter-spacing:-.02em;margin:2px 0}.ccx-state{display:flex;align-items:center;gap:8px;padding:8px 10px;border:1px solid var(--border);border-radius:9px}.ccx-state>i{width:7px;height:7px;border-radius:50%;background:var(--surface-2)}.ccx-state.is-live>i{background:#10b981}.ccx-state.is-sandbox>i,.ccx-state.is-saved>i{background:#f59e0b}.ccx-state span b,.ccx-state span small{display:block;font-size:8.5px}.ccx-state span small{color:var(--text-muted);font-weight:400;margin-top:2px}.ccx-readiness{display:flex;align-items:center;gap:8px;margin-top:12px;border-left:3px solid var(--surface-2);background:var(--surface-1);padding:8px 10px}.ccx-readiness.is-mapped{border-left-color:var(--frd-accent)}.ccx-readiness span{font-size:9px;font-weight:750}.ccx-readiness small{font-size:8.5px;color:var(--text-muted)}.ccx-facts{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:11px}.ccx-facts>div{border:1px solid var(--border);border-radius:9px;padding:10px}.ccx-facts b{display:block;font-size:9px;margin-top:4px}.ccx-capabilities{margin-top:13px}.ccx-capabilities>div{display:flex;gap:6px;flex-wrap:wrap;margin-top:6px}.ccx-capabilities b{font-size:8px;background:var(--surface-1);border:1px solid var(--border);padding:5px 8px;border-radius:999px;font-weight:650}.ccx-form{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:14px}.ccx-form label{display:grid;gap:5px}.ccx-form input,.ccx-form select{width:100%;border:1px solid var(--border);background:var(--surface-0);color:inherit;border-radius:9px;padding:10px;font-size:10px;outline:none}.ccx-form input:focus,.ccx-form select:focus{border-color:var(--frd-accent);box-shadow:0 0 0 3px color-mix(in srgb,var(--frd-accent) 12%,transparent)}.ccx-security{display:flex;gap:9px;align-items:flex-start;background:var(--surface-1);border-radius:9px;margin-top:12px;padding:10px}.ccx-security>i{font-style:normal;color:var(--frd-accent)}.ccx-security p{font-size:8.5px;color:var(--text-muted);line-height:1.5;margin:0}.ccx-security b{color:var(--text-primary)}.ccx-notice{display:block;margin-top:10px;border-left:3px solid var(--frd-accent);background:color-mix(in srgb,var(--frd-accent) 5%,var(--surface-1));padding:8px 10px;font-size:9px}.ccx main>footer{display:flex;justify-content:flex-end;gap:8px;margin-top:13px}.ccx main>footer button{border-radius:9px;padding:10px 13px;font-size:9.5px;font-weight:700;cursor:pointer}.ccx-secondary{border:1px solid var(--border);background:transparent;color:inherit}.ccx-primary{border:1px solid var(--frd-accent);background:var(--frd-accent);color:white;box-shadow:0 10px 24px -14px var(--frd-accent)}
@media(max-width:760px){.ccx-head{align-items:stretch;flex-direction:column}.ccx-head-actions{align-items:stretch}.ccx-head-actions>label{width:100%}.ccx-head-actions>button span{display:none}.ccx-workspace{grid-template-columns:1fr}.ccx nav{display:flex;border-right:0;border-bottom:1px solid var(--border);overflow:auto}.ccx nav button{min-width:160px}.ccx nav .ccx-custom{margin:0;border:0}.ccx-facts,.ccx-form{grid-template-columns:1fr}.ccx main>header{align-items:flex-start;gap:10px}.ccx-state{max-width:170px}.ccx main>footer{flex-direction:column}.ccx main>footer button{width:100%}}
`
